import axiosInstance from './axios-instance';
import { isMutationSuccess } from './cache-utils';
import { invalidateOrderDataCache, resolveClientId, resolveOrderId } from './cache-invalidation';

const getScopedAccountId = (accountId?: number): number => {
  if (typeof window === 'undefined') {
    return Number(accountId || 0);
  }
  const ownerAccountId = Number(localStorage.getItem('ownerAccountId') || '0');
  if (ownerAccountId > 0) {
    return ownerAccountId;
  }
  if (Number(accountId) > 0) {
    return Number(accountId);
  }
  return Number(localStorage.getItem('accountId') || '0');
};

const withScopedAccountId = (payload: any) => {
  if (!payload || typeof payload !== 'object') {
    return payload;
  }

  const scopedAccountId = getScopedAccountId(payload.accountId);
  if (!(scopedAccountId > 0)) {
    return payload;
  }

  const patchState = (state: any) =>
    state && typeof state === 'object'
      ? {
          ...state,
          accountId: scopedAccountId,
          draperyOrder:
            state.draperyOrder && typeof state.draperyOrder === 'object'
              ? { ...state.draperyOrder, accountId: scopedAccountId }
              : state.draperyOrder,
          workOrder:
            state.workOrder && typeof state.workOrder === 'object'
              ? { ...state.workOrder, accountId: scopedAccountId }
              : state.workOrder,
        }
      : state;

  return patchState({
    ...payload,
    stateObj: patchState(payload.stateObj),
    copyState: patchState(payload.copyState),
  });
};

const addDraperyOrderPrice = async (payload: any, workroomSettings: any, client: any) => {
  try {
    const scopedPayload = withScopedAccountId(payload);
    const response = await axiosInstance.post('/drapery-order-price', {
      data: scopedPayload,
      workroomSettings,
      client,
    });
    if (isMutationSuccess(response.data)) {
      invalidateOrderDataCache(resolveOrderId(scopedPayload, response.data), resolveClientId(scopedPayload, response.data));
    }

    return response.data;
  } catch (error) {
    console.error('Error adding drapery order price:', error);
    throw error;
  }
};

const addRomanShadeOrderPrice = async (payload: any, workroomSettings: any, client: any) => {
  try {
    const scopedPayload = withScopedAccountId(payload);
    const response = await axiosInstance.post('/drapery-order-price/roman-shade', {
      data: scopedPayload,
      workroomSettings,
      client,
    });
    if (isMutationSuccess(response.data)) {
      invalidateOrderDataCache(resolveOrderId(scopedPayload, response.data), resolveClientId(scopedPayload, response.data));
    }

    return response.data;
  } catch (error) {
    console.error('Error adding roman shade order price:', error);
    throw error;
  }
};

const updateDraperyOrderPrice = async (
  payload: any,
  workroomSettings: any,
  id: number,
  client: any
) => {
  try {
    const scopedPayload = withScopedAccountId(payload);
    const response = await axiosInstance.put('/drapery-order-price', {
      data: scopedPayload,
      workroomSettings,
      id,
      client,
    });
    if (isMutationSuccess(response.data)) {
      invalidateOrderDataCache(resolveOrderId(scopedPayload, response.data), resolveClientId(scopedPayload, response.data));
    }

    return response.data;
  } catch (error) {
    console.error('Error while updating drapery order price:', error);
    throw error;
  }
};

const updateRomanShadeOrderPricing = async (
  payload: any,
  workroomSettings: any,
  id: any,
  client: any
) => {
  try {
    const scopedPayload = withScopedAccountId(payload);
    const response = await axiosInstance.put('/drapery-order-price/roman-shade', {
      data: scopedPayload,
      pricingId: id,
      workroomSettings: workroomSettings,
      client,
    });
    if (isMutationSuccess(response.data)) {
      invalidateOrderDataCache(resolveOrderId(scopedPayload, response.data), resolveClientId(scopedPayload, response.data));
    }
    return response.data;
  } catch (error) {
    console.error('Error while updating roman shade order price:', error);
    throw error;
  }
};

const getDraperyOrderPrice = async (id: number) => {
  try {
    const response = await axiosInstance.get(`/drapery-order-price/${id}`);

    return response.data;
  } catch (error) {
    console.error('Error getting drapery order price:', error);
    throw error;
  }
};

export {
  addDraperyOrderPrice,
  getDraperyOrderPrice,
  updateDraperyOrderPrice,
  updateRomanShadeOrderPricing,
  addRomanShadeOrderPrice,
};
